import { useState } from 'react'
import { MessageSquare, Send, Tag, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../../lib/supabase'
import { useAppStore } from '../../stores/app'
import { toast } from 'sonner'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

interface FeedbackFormProps {
  contentId: string
  contentBody: string
}

const FEEDBACK_TYPES = [
  { key: 'tone', label: 'Tono', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { key: 'accuracy', label: 'Precisión', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'structure', label: 'Estructura', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'style', label: 'Estilo', color: 'bg-pink-100 text-pink-700 border-pink-200' },
]

function useFeedback(contentId: string) {
  return useQuery({
    queryKey: ['content_feedback', contentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_feedback')
        .select('*')
        .eq('content_id', contentId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
    enabled: !!contentId,
  })
}

export function FeedbackForm({ contentId, contentBody }: FeedbackFormProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [feedbackType, setFeedbackType] = useState<string>('tone')
  const [notes, setNotes] = useState('')
  const currentUser = useAppStore((state) => state.currentUser)
  const queryClient = useQueryClient()

  const { data: feedbackHistory } = useFeedback(contentId)

  const submitMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('content_feedback').insert({
        content_id: contentId,
        user_id: currentUser?.id,
        original_content: contentBody,
        edit_type: feedbackType,
        feedback_notes: notes,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content_feedback', contentId] })
      toast.success('Feedback enviado')
      setNotes('')
      setIsOpen(false)
    },
    onError: () => {
      toast.error('Error al enviar feedback')
    },
  })

  return (
    <div className="space-y-4">
      {/* Feedback button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
      >
        <MessageSquare className="w-4 h-4 text-slate-400" />
        Dar Feedback ({feedbackHistory?.length ?? 0})
      </button>

      {/* Feedback form */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-slate-700">Nuevo Feedback</h4>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Type selector */}
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">
                  <Tag className="w-3 h-3 inline mr-1" />
                  Tipo de feedback
                </label>
                <div className="flex flex-wrap gap-2">
                  {FEEDBACK_TYPES.map((type) => (
                    <button
                      key={type.key}
                      onClick={() => setFeedbackType(type.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        feedbackType === type.key
                          ? `${type.color} ring-2 ring-offset-1 ring-slate-300`
                          : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Notas</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Describe el feedback o sugerencia de mejora..."
                  className="input text-sm min-h-[80px] resize-y"
                  rows={3}
                />
              </div>

              {/* Submit */}
              <button
                onClick={() => submitMutation.mutate()}
                disabled={!notes.trim() || submitMutation.isPending}
                className="btn btn-primary flex items-center gap-2 text-sm w-full justify-center"
              >
                <Send className="w-4 h-4" />
                {submitMutation.isPending ? 'Enviando...' : 'Enviar Feedback'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feedback history - always visible, expanded by default */}
      <div className="border border-slate-200 rounded-lg bg-slate-50/80 overflow-hidden">
        <h4 className="text-sm font-semibold text-slate-700 px-4 py-3 border-b border-slate-200 bg-white/80">
          Historial de Feedback ({feedbackHistory?.length ?? 0})
        </h4>
        <div className="p-3 space-y-3 max-h-[320px] overflow-y-auto">
          {feedbackHistory && feedbackHistory.length > 0 ? (
            feedbackHistory.map((fb) => {
              const fbRecord = fb as Record<string, string>
              const typeConfig = FEEDBACK_TYPES.find((t) => t.key === fbRecord.edit_type)
              return (
                <div
                  key={fbRecord.id}
                  className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    {typeConfig && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded border ${typeConfig.color}`}
                      >
                        {typeConfig.label}
                      </span>
                    )}
                    <span className="text-xs text-slate-400">
                      {formatDistanceToNow(new Date(fbRecord.created_at), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </span>
                  </div>
                  {fbRecord.feedback_notes && (
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {fbRecord.feedback_notes}
                    </p>
                  )}
                </div>
              )
            })
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">
              Aún no hay comentarios. Usa &quot;Dar Feedback&quot; para añadir el primero.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
